import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { can } from '../auth';

function TierModal({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(existing || {
    name: '', price: '', min_weight: '', max_weight: '',
    max_length: '', max_width: '', max_height: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) return toast.error('Name and price are required');
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/price-tiers/${existing.id}`, form);
        toast.success('Tier updated');
      } else {
        await api.post('/price-tiers', form);
        toast.success('Tier created');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save tier'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-auto my-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-800">{isEdit ? 'Edit Price Tier' : 'Add Price Tier'}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tier Name *</label>
              <input type="text" value={form.name} onChange={set('name')} placeholder="e.g. Small, Medium, Large"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Price (R) *</label>
              <input type="number" step="0.01" min="0" value={form.price} onChange={set('price')} placeholder="0.00"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Weight Range (kg)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Weight</label>
                <input type="number" step="0.1" min="0" value={form.min_weight} onChange={set('min_weight')} placeholder="0"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Weight</label>
                <input type="number" step="0.1" min="0" value={form.max_weight} onChange={set('max_weight')} placeholder="e.g. 5"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Max Dimensions (cm) — optional</p>
            <div className="grid grid-cols-3 gap-3">
              {[['max_length','Length'],['max_width','Width'],['max_height','Height']].map(([k,lbl]) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
                  <input type="number" step="1" min="0" value={form[k]} onChange={set(k)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Tier')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Pricing() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // null | 'add' | tier object

  const { data: tiers = [], isLoading } = useQuery({
    queryKey: ['price-tiers-all'],
    queryFn: () => api.get('/price-tiers').then(r => r.data),
  });

  const deactivate = async (tier) => {
    if (!window.confirm(`Deactivate "${tier.name}"? It will no longer appear when creating parcels.`)) return;
    try {
      await api.patch(`/price-tiers/${tier.id}`, { ...tier, active: false });
      toast.success('Tier deactivated');
      qc.invalidateQueries({ queryKey: ['price-tiers-all'] });
      qc.invalidateQueries({ queryKey: ['price-tiers'] });
    } catch { toast.error('Failed to deactivate tier'); }
  };

  const saved = () => {
    setModal(null);
    qc.invalidateQueries({ queryKey: ['price-tiers-all'] });
    qc.invalidateQueries({ queryKey: ['price-tiers'] });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {modal && (
        <TierModal
          existing={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={saved}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Parcel Pricing</h1>
          <p className="text-xs text-gray-400 mt-0.5">Manage price tiers used when creating parcels</p>
        </div>
        {can('system.settings') && (
          <button onClick={() => setModal('add')}
            className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <Plus size={16} /> Add Tier
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : tiers.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No price tiers yet</p>
            <p className="text-xs text-gray-400 mt-1">Add your first tier to start pricing parcels</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3 text-center">Weight (kg)</th>
                  <th className="px-4 py-3 text-center">Dimensions (cm)</th>
                  {can('system.settings') && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {tiers.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">{t.name}</td>
                    <td className="px-4 py-3 text-right font-bold text-orange-600">R {parseFloat(t.price).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center text-gray-500 text-xs">
                      {t.min_weight || t.max_weight
                        ? `${t.min_weight || 0} – ${t.max_weight || '∞'} kg`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 text-xs">
                      {t.max_length || t.max_width || t.max_height
                        ? `${t.max_length || '?'} × ${t.max_width || '?'} × ${t.max_height || '?'}`
                        : '—'}
                    </td>
                    {can('system.settings') && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button onClick={() => setModal(t)} className="text-xs text-orange-500 hover:underline">Edit</button>
                          <button onClick={() => deactivate(t)} className="text-xs text-gray-400 hover:text-red-500 hover:underline">Remove</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
